import React from "react";
import * as Icon from "react-feather";

import "./tools-inline-edit.css";
import WComponent from "../../../common/WComponent";

/**
 * A value on a statement that can be changed where it is read.
 *
 * A preparer notices the wording is wrong while looking at the statement, so the change belongs
 * there rather than behind a form on another screen. The value renders as ordinary statement text
 * until it is hovered, which is the point — a page covered in input boxes stops reading like a
 * financial statement.
 *
 * Escape abandons the edit and Enter commits it, because a click elsewhere is ambiguous: it can mean
 * "I have finished" or "I have changed my mind", and a value printed on a client's statements should
 * not be changed by an ambiguous gesture. Blur commits, matching every other field in the tool, but
 * only when the text actually differs.
 *
 * Enter and the blur that follows it are two events for one decision, so a commit already running
 * blocks the second. Without that the same change is saved twice, and the two saves race over what
 * the statements end up carrying.
 *
 * Clearing the box restores whatever the statements would say on their own, rather than storing an
 * empty name — the caller decides what that default is and shows it as the placeholder.
 *
 * `value` is what gets edited and saved; `displayValue` is what is read when the field is at rest.
 * They differ wherever the stored form is not the printed form — a report date is stored as an ISO
 * date so the browser's date control can enforce it, and printed as "June 30, 2026". Editing the
 * printed form as free text would let somebody save a date nothing can parse.
 */
class ToolsInlineEdit extends WComponent {
	state = { editing: false, draft: "", committing: false };

	startEditing = () => {
		const { value, disabled } = this.props;
		if (disabled) return;
		this.update({ editing: true, draft: value || "" });
	};

	cancelEditing = () => this.update({ editing: false, draft: "" });

	commitEditing = async () => {
		const { value, onChange } = this.props;
		const { draft, committing } = this.state;
		if (committing) return;

		const trimmed = draft.trim();
		await this.update({ editing: false, committing: true });
		try {
			if (trimmed === (value || "").trim()) return;
			if (onChange) await onChange({ value: trimmed });
		} finally {
			await this.update({ committing: false });
		}
	};

	onKeyDown = event => {
		if (event.key === "Enter") {
			event.preventDefault();
			this.commitEditing();
			return;
		}
		if (event.key === "Escape") {
			event.preventDefault();
			this.cancelEditing();
		}
	};

	renderInput = () => {
		const { placeholder, type } = this.props;
		const { draft } = this.state;

		return (
			<input
				className="tk-inline-edit__input"
				type={type || "text"}
				autoFocus
				value={draft}
				placeholder={placeholder}
				onChange={e => this.update({ draft: e.target.value })}
				onKeyDown={this.onKeyDown}
				onBlur={this.commitEditing}
				spellCheck={true}
			/>
		);
	};

	render() {
		const { value, displayValue, placeholder, disabled, label } = this.props;
		const { editing } = this.state;

		if (editing) return this.renderInput();

		const readable = displayValue !== undefined ? displayValue : value;
		const shown = readable || placeholder || "";
		const isPlaceholder = !readable;

		return (
			<button
				type="button"
				className={"tk-inline-edit" + (isPlaceholder ? " tk-inline-edit--empty" : "") + (disabled ? " tk-inline-edit--disabled" : "")}
				onClick={this.startEditing}
				disabled={disabled}
				title={disabled ? null : "Click to change " + (label || "this")}
			>
				<span className="tk-inline-edit__value">{shown}</span>
				{!disabled && <Icon.Edit2 size={11} className="tk-inline-edit__pencil" />}
			</button>
		);
	}
}

export default ToolsInlineEdit;
